import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { waitForImages } from '@/lib/waitForImages';
import { PartnerProfileDocument, type PartnerProfileData } from '@/components/partners/PartnerProfileDocument';
import { PartnerAccountPanel } from '@/components/partners/PartnerAccountPanel';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

const SIGNED_URL_TTL = 60 * 60;

async function pathToSignedUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { data } = await supabase.storage.from('invoice-assets').createSignedUrl(path, SIGNED_URL_TTL);
  return data?.signedUrl ?? null;
}

export default function ResellerProfileView() {
  const { partnerId } = useParams<{ partnerId: string }>();
  const navigate = useNavigate();
  const [partner, setPartner] = useState<PartnerProfileData | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [issuer, setIssuer] = useState<{ address: string | null; contact: string | null; email: string | null }>({
    address: null,
    contact: null,
    email: null,
  });
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const docRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!partnerId) return;
    setLoading(true);
    const [{ data }, { data: tpl }] = await Promise.all([
      supabase
        .from('core_partners')
        .select(
          'name, designation, type, status, city, contact_person, phone, email, address, show_on_website, hours, map_link, description, drug_license_no, tin_no, nid_no, bin_no, agreement_terms, agreement_start_date, agreement_end_date, documents, created_at',
        )
        .eq('id', partnerId)
        .maybeSingle(),
      // Same brand identity (logo + company address/contact/email) used on
      // invoices/receipts (ERP > Settings > Invoice Identity), so the
      // profile carries the full official wordmark and our own company
      // details, matching ERP's version of this same page exactly.
      supabase.from('invoice_templates').select('logo_url, issuer_address, issuer_contact, issuer_email').eq('doc_type', 'invoice').maybeSingle(),
    ]);
    setPartner((data as PartnerProfileData) ?? null);
    setLogoUrl(await pathToSignedUrl((tpl?.logo_url as string | null) ?? null));
    setIssuer({
      address: (tpl?.issuer_address as string | null) ?? null,
      contact: (tpl?.issuer_contact as string | null) ?? null,
      email: (tpl?.issuer_email as string | null) ?? null,
    });
    setLoading(false);
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = async () => {
    setPrinting(true);
    if (docRef.current) await waitForImages(docRef.current);
    window.print();
    setPrinting(false);
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!partner) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Partner not found" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-4">
        <div className="flex items-center justify-between print:hidden">
          <button onClick={() => navigate('/admin/resellers')} className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-faint hover:text-ink cursor-pointer">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Resellers
          </button>
          <Button onClick={handlePrint} loading={printing}>
            <Printer className="w-4 h-4" /> Print (A4)
          </Button>
        </div>

        {partnerId && (
          <div className="space-y-4 print:hidden">
            <PartnerAccountPanel partnerId={partnerId} partnerName={partner.name} />
          </div>
        )}

        <div className="rounded-2xl bg-[#EEF1F6] p-8 overflow-auto print:p-0 print:bg-white print:rounded-none">
          <div ref={docRef} className="shadow-[0_8px_40px_-8px_rgba(27,42,74,0.35)] print:shadow-none mx-auto" style={{ width: 'fit-content' }}>
            <PartnerProfileDocument partner={partner} logoUrl={logoUrl} issuer={issuer} />
          </div>
        </div>
      </div>
    </AppShell>
  );
}
